import React from 'react'
import { Grid, Card, CardContent, Typography, Skeleton, Box, Paper, Stack, useMediaQuery, useTheme, Divider, Tooltip } from '@mui/material'
import { PlanningStatsDto } from '@/core/models/planning.model'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import PeopleIcon from '@mui/icons-material/People'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import { useLingui } from '@lingui/react'
import { t } from '@lingui/macro'

interface PlanningStatisticsProps {
  stats?: PlanningStatsDto
  isLoading: boolean
}

export function PlanningStatistics({ stats, isLoading }: PlanningStatisticsProps) {
  useLingui()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  if (isMobile) {
    return (
      <Paper data-testid="planning-stats-strip" variant="outlined" sx={{ p: 1, mb: 1.5 }}>
        <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} sx={{ minWidth: 0 }}>
          <Metric title={t`Total Plannings`} value={stats?.totalPlannings || 0} icon={<CalendarTodayIcon fontSize="small" />} isLoading={isLoading} />
          <Metric title={t`Shift Templates`} value={stats?.totalShiftTemplates || 0} icon={<EventAvailableIcon fontSize="small" />} isLoading={isLoading} />
          <Metric title={t`Shifts Created`} value={stats?.totalShifts || 0} icon={<PeopleIcon fontSize="small" />} isLoading={isLoading} />
          <Metric title={t`Completion Rate`} value={`${Math.round(stats?.overallCompletionRate || 0)}%`} icon={<AssignmentTurnedInIcon fontSize="small" />} isLoading={isLoading} />
        </Stack>
      </Paper>
    )
  }

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={6} md={3}>
        <StatsCard
          title={t`Total Plannings`}
          value={stats?.totalPlannings || 0}
          subtitle={t`${stats?.activePlannings ?? 0} active, ${stats?.upcomingPlannings ?? 0} upcoming`}
          icon={<CalendarTodayIcon />}
          isLoading={isLoading}
          color="primary"
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatsCard
          title={t`Shift Templates`}
          value={stats?.totalShiftTemplates || 0}
          subtitle={t`${stats?.totalPossibleShifts || 0} possible shifts`}
          icon={<EventAvailableIcon />}
          isLoading={isLoading}
          color="secondary"
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatsCard
          title={t`Shifts Created`}
          value={stats?.totalShifts || 0}
          subtitle={t`${stats?.staffedShifts || 0} of ${stats?.totalPossibleShifts || 0} staffed`}
          icon={<PeopleIcon />}
          isLoading={isLoading}
          color="info"
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <StatsCard
          title={t`Completion Rate`}
          value={`${Math.round(stats?.overallCompletionRate || 0)}%`}
          subtitle={t`${stats?.highAttentionCount || 0} plannings need attention`}
          icon={<AssignmentTurnedInIcon />}
          isLoading={isLoading}
          color={getCompletionRateColor(stats?.overallCompletionRate || 0)}
        />
      </Grid>
    </Grid>
  )
}

function getCompletionRateColor(rate: number): 'success' | 'warning' | 'error' {
  if (rate >= 80) return 'success'
  if (rate >= 50) return 'warning'
  return 'error'
}

function Metric({ icon, isLoading, title, value }: { icon: React.ReactNode; isLoading: boolean; title: string; value: number | string }) {
  return (
    <Tooltip title={title}>
      <Box aria-label={isLoading ? title : `${title}: ${value}`} sx={{ flex: 1, minWidth: 0, px: 0.75, textAlign: 'center' }}>
        <Stack alignItems="center" spacing={0.25}>
          <Box sx={{ color: 'text.secondary', display: 'flex', height: 18 }}>{icon}</Box>
          {isLoading ? (
            <Skeleton variant="text" width="60%" sx={{ mx: 'auto' }} />
          ) : (
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.15} noWrap>
              {value}
            </Typography>
          )}
        </Stack>
      </Box>
    </Tooltip>
  )
}

interface StatsCardProps {
  title: string
  value: string | number
  subtitle: string
  icon: React.ReactNode
  isLoading: boolean
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'
}

function StatsCard({ title, value, subtitle, icon, isLoading, color }: StatsCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" color="text.secondary">
            {title}
          </Typography>
          <Box sx={{ color: `${color}.main` }}>{icon}</Box>
        </Box>
        {isLoading ? (
          <Skeleton variant="rectangular" width="100%" height={60} />
        ) : (
          <>
            <Typography variant="h3" sx={{ mb: 1 }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  )
}
